import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface SalesUnitMember {
  user_id: string;
  user?: {
    full_name: string;
    avatar_url: string | null;
    email: string;
  } | null;
}

export interface SalesUnit {
  id: string;
  name: string;
  bf_target_mnok: number | null;
  year: number;
  members?: SalesUnitMember[];
}

/** Fetch all sales units for a given year (or all years if not specified) */
export function useSalesUnits(year?: number) {
  return useQuery({
    queryKey: ["sales-units", year],
    queryFn: async (): Promise<SalesUnit[]> => {
      let query = supabase
        .from("sales_units")
        // Alias relasjonen som "members" så type/UI-koden får riktig navn.
        // Uten alias returnerer Supabase nøkkelen som "sales_unit_members" og
        // u.members blir undefined → tom array → backend rejecter prosjektet.
        .select("id, name, bf_target_mnok, year, members:sales_unit_members(user_id, user:users(full_name, avatar_url, email))");

      if (year) {
        query = query.eq("year", year);
      }

      const { data, error } = await query.order("name");
      if (error) throw error;
      return (data as unknown as SalesUnit[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch members of a specific sales unit */
export function useSalesUnitMembers(salesUnitId: string | undefined) {
  return useQuery({
    queryKey: ["sales-unit-members", salesUnitId],
    enabled: !!salesUnitId,
    queryFn: async (): Promise<SalesUnitMember[]> => {
      const { data, error } = await supabase
        .from("sales_unit_members")
        .select("user_id, user:users(full_name, avatar_url, email)")
        .eq("sales_unit_id", salesUnitId!);

      if (error) throw error;
      return (data as unknown as SalesUnitMember[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Map sales_unit_id -> SalesUnit */
export function buildSalesUnitMap(units: SalesUnit[]): Map<string, SalesUnit> {
  const map = new Map<string, SalesUnit>();
  for (const u of units) {
    map.set(u.id, u);
  }
  return map;
}
